import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Bot, Database, KeyRound, Layers, ListOrdered, CreditCard, AlertTriangle, FileText } from "lucide-react";
import { api, formatApiError, fmtBytes, fmtPay } from "../lib/api";
import { GlassCard, Spinner } from "../components/common";
import { fmtStorage } from "../lib/planStyles";

const JOB_STATUS = {
  queued: ["Na fila", "text-amber-300"], running: ["Importando", "text-cyan-300"],
  done: ["Concluído", "text-emerald-300"], failed: ["Erro", "text-rose-300"], canceled: ["Cancelado", "text-slate-400"],
};
const PAY_STATUS = {
  approved: ["Aprovado", "text-emerald-300"], pending: ["Pendente", "text-amber-300"],
  cancelled: ["Cancelado", "text-slate-500"], rejected: ["Recusado", "text-rose-300"], refunded: ["Estornado", "text-slate-400"],
};

const fmtDT = (v) => {
  if (!v) return "—";
  const d = typeof v === "number" ? new Date(v * 1000) : new Date(v);
  return isNaN(d) ? "—" : d.toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export default function AdminCliente() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/admin/clients/${id}/detail`)
      .then((r) => setD(r.data))
      .catch((e) => setErr(formatApiError(e)));
  }, [id]);

  if (err) return <p className="text-sm text-rose-300" data-testid="admin-client-error">{err}</p>;
  if (!d) return <div className="flex justify-center py-24"><Spinner /></div>;

  const u = d.user;
  const quotaGb = u.plan_storage_gb || 0;
  const usedGb = (u.storage_used_bytes || 0) / 1e9;
  const pct = quotaGb ? Math.min(100, Math.round((usedGb / quotaGb) * 100)) : 0;

  return (
    <div className="space-y-6" data-testid="admin-client-page">
      <button onClick={() => navigate("/app/admin")} className="text-xs text-slate-500 hover:text-cyan-300 inline-flex items-center gap-1.5" data-testid="admin-client-back">
        <ArrowLeft size={13} /> Painel do Administrador
      </button>

      <GlassCard className="p-6" data-testid="admin-client-header">
        <div className="flex flex-wrap items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center shrink-0">
            <span className="text-sm font-bold text-cyan-300">{(u.name || "?").slice(0, 2).toUpperCase()}</span>
          </div>
          <div className="flex-1 min-w-48">
            <h1 className="font-display text-lg font-bold text-white" data-testid="admin-client-name">{u.name}</h1>
            <p className="text-xs text-slate-500">{u.email} · {u.role === "super_admin" ? "Administrador" : "Revendedor"} · {u.status === "suspended" ? "🔴 suspenso" : "🟢 ativo"}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-400" data-testid="admin-client-plan">{u.plan_name || "Sem plano"}{u.plan_expires_at ? ` · vence ${fmtDT(u.plan_expires_at).split(" ")[0]}` : ""}</p>
            <p className="text-[10px] font-mono2 text-slate-500">cadastro {fmtDT(u.created_at)}</p>
          </div>
        </div>
        <div className="mt-4">
          <div className="flex justify-between text-[10px] font-mono2 text-slate-500 mb-1.5">
            <span>Armazenamento utilizado</span>
            <span data-testid="admin-client-storage">{fmtBytes(u.storage_used_bytes || 0)} de {quotaGb ? fmtStorage(quotaGb) : "sem cota"}</span>
          </div>
          <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
            <div className={`h-full ${pct > 90 ? "bg-rose-400" : "bg-cyan-400"}`} style={{ width: `${pct}%` }} />
          </div>
        </div>
      </GlassCard>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="admin-client-counts">
        {[
          { label: "Bots", value: d.bots.length, icon: Bot },
          { label: "Arquivos", value: d.counts.versions, icon: Database },
          { label: "Títulos", value: d.counts.titles, icon: Layers },
          { label: "Acessos", value: d.counts.accesses, icon: KeyRound },
          { label: "Processos", value: d.counts.jobs, icon: ListOrdered },
        ].map((c) => (
          <GlassCard key={c.label} className="p-4">
            <c.icon size={15} className="text-cyan-400" />
            <p className="font-display text-lg font-bold text-white mt-1.5">{c.value}</p>
            <p className="text-[9px] font-mono2 uppercase tracking-widest text-slate-500">{c.label}</p>
          </GlassCard>
        ))}
      </div>

      <GlassCard className="p-6" data-testid="admin-client-accesses">
        <h3 className="font-display text-white font-semibold text-sm mb-4 flex items-center gap-2"><KeyRound size={15} className="text-cyan-400" /> Acessos do cliente ({d.accesses.length})</h3>
        {d.accesses.length === 0 ? <p className="text-xs text-slate-600">Nenhum acesso criado.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[720px]">
              <thead>
                <tr className="text-left text-[9px] font-mono2 uppercase tracking-widest text-slate-500 border-b border-slate-800/60">
                  <th className="py-2 pr-3">Nome</th><th className="py-2 pr-3">Usuário</th><th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">IP travado</th><th className="py-2 pr-3">Último IP</th><th className="py-2 pr-3">Dispositivo</th><th className="py-2">Último acesso</th>
                </tr>
              </thead>
              <tbody>
                {d.accesses.map((a) => (
                  <tr key={a.id} className="border-b border-slate-800/40 last:border-0" data-testid={`admin-client-access-${a.id}`}>
                    <td className="py-2.5 pr-3 text-white font-medium">{a.name || "—"}</td>
                    <td className="py-2.5 pr-3 font-mono2 text-slate-400">{a.username}</td>
                    <td className={`py-2.5 pr-3 font-mono2 ${a.status === "active" ? "text-emerald-300" : "text-amber-300"}`}>{a.status === "active" ? "ativo" : "pausado"}</td>
                    <td className="py-2.5 pr-3 font-mono2 text-slate-300">{a.allowed_ip || <span className="text-slate-600">livre</span>}</td>
                    <td className="py-2.5 pr-3 font-mono2 text-slate-300">{a.last_ip || "—"}</td>
                    <td className="py-2.5 pr-3 text-slate-400">{a.last_device || "—"}</td>
                    <td className="py-2.5 font-mono2 text-slate-400">{fmtDT(a.last_access_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      <GlassCard className="p-6" data-testid="admin-client-queue">
        <h3 className="font-display text-white font-semibold text-sm mb-4 flex items-center gap-2"><ListOrdered size={15} className="text-cyan-400" /> Fila de importação ({d.jobs.length + d.uploads.length})</h3>
        {d.jobs.length === 0 && d.uploads.length === 0 ? <p className="text-xs text-slate-600">Nenhum processo.</p> : (
          <div className="space-y-2">
            {d.uploads.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center gap-2 text-xs p-2.5 rounded-lg bg-slate-950/50 border border-slate-800/60" data-testid={`admin-client-upload-${s.id}`}>
                <span className="text-cyan-300 font-mono2">Importando</span>
                <span className="text-white truncate flex-1">{s.filename}</span>
                <span className="font-mono2 text-slate-500">upload web · {fmtBytes(s.size_bytes)}</span>
              </div>
            ))}
            {d.jobs.map((j) => {
              const [lbl, cls] = JOB_STATUS[j.status] || ["—", "text-slate-400"];
              return (
                <div key={j.id} className="flex flex-wrap items-center gap-2 text-xs p-2.5 rounded-lg bg-slate-950/50 border border-slate-800/60" data-testid={`admin-client-job-${j.id}`}>
                  <span className={`font-mono2 ${cls}`}>{lbl}</span>
                  <span className="text-white truncate flex-1">#{j.seq || "?"} · {j.kind_filter} · {j.processed || 0}/{j.total || 0}</span>
                  {(j.failed_count || 0) > 0 && <span className="text-rose-300 font-mono2">{j.failed_count} erros</span>}
                  <span className="font-mono2 text-slate-500">{fmtDT(j.created_at)}</span>
                </div>
              );
            })}
          </div>
        )}
      </GlassCard>

      <GlassCard className="p-6" data-testid="admin-client-payments">
        <h3 className="font-display text-white font-semibold text-sm mb-4 flex items-center gap-2"><CreditCard size={15} className="text-cyan-400" /> Histórico de pagamentos ({d.payments.length + d.invoices.length})</h3>
        {d.payments.length === 0 && d.invoices.length === 0 ? <p className="text-xs text-slate-600">Nenhum pagamento.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[560px]">
              <thead>
                <tr className="text-left text-[9px] font-mono2 uppercase tracking-widest text-slate-500 border-b border-slate-800/60">
                  <th className="py-2 pr-3">Data</th><th className="py-2 pr-3">Descrição</th><th className="py-2 pr-3">Valor</th><th className="py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {d.payments.map((p) => {
                  const [lbl, cls] = PAY_STATUS[p.status] || [p.status || "—", "text-slate-400"];
                  return (
                    <tr key={p.id || p.external_reference} className="border-b border-slate-800/40 last:border-0" data-testid={`admin-client-payment-${p.id}`}>
                      <td className="py-2.5 pr-3 font-mono2 text-slate-400">{fmtDT(p.created_at)}</td>
                      <td className="py-2.5 pr-3 text-slate-300">{p.title || p.plan_name || p.package_name || p.description || "Pagamento"}</td>
                      <td className="py-2.5 pr-3 font-mono2 text-white">{fmtPay(p)}</td>
                      <td className={`py-2.5 font-mono2 ${cls}`}>{lbl}</td>
                    </tr>
                  );
                })}
                {d.invoices.map((inv) => {
                  const [lbl, cls] = PAY_STATUS[inv.status] || [inv.status, "text-slate-400"];
                  return (
                    <tr key={inv.id} className="border-b border-slate-800/40 last:border-0" data-testid={`admin-client-invoice-${inv.id}`}>
                      <td className="py-2.5 pr-3 font-mono2 text-slate-400">{fmtDT(inv.created_at)}</td>
                      <td className="py-2.5 pr-3 text-slate-300 flex items-center gap-1.5"><FileText size={11} className="text-slate-500" /> {inv.title}</td>
                      <td className="py-2.5 pr-3 font-mono2 text-white">{fmtPay(inv)}</td>
                      <td className={`py-2.5 font-mono2 ${cls}`}>{lbl}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      {d.errors.length > 0 && (
        <GlassCard className="p-6" data-testid="admin-client-errors">
          <h3 className="font-display text-white font-semibold text-sm mb-4 flex items-center gap-2"><AlertTriangle size={15} className="text-rose-400" /> Erros recentes ({d.errors.length})</h3>
          <div className="space-y-2">
            {d.errors.slice(0, 10).map((j) => (
              <div key={j.id} className="text-xs p-2.5 rounded-lg bg-rose-400/5 border border-rose-400/20">
                <p className="text-rose-300 font-mono2">#{j.seq || "?"} · {j.kind_filter} · {fmtDT(j.created_at)}</p>
                {j.error && <p className="text-rose-200/90 mt-1">{j.error}</p>}
                {(j.errors || []).slice(-2).map((e, i) => <p key={i} className="text-slate-400 font-mono2 mt-0.5 break-all">{e}</p>)}
              </div>
            ))}
          </div>
        </GlassCard>
      )}
    </div>
  );
}
